import { useState, useRef, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { Send, Loader2, Settings, Sparkles, Database, Bot, PlusCircle, Trash, MessageSquare, Upload, X, Mic, Cloud, HardDrive } from 'lucide-react';
import { openAIService, OpenAIMessage } from '@/lib/ai/openai-service';
import { vectorStore, isUsingSupabase } from '@/lib/ai/vector-store-provider';
import { initializeRAGSystem, loadTrainingDataFromStorage } from '@/lib/ai/init-rag';
import { v4 as uuidv4 } from 'uuid';
import { AIConfig } from '@/components/ai/ai-config';
import { TrainingDataUploader } from '@/components/ai/training-data-uploader';
import { KnowledgeManager } from '@/components/ai/knowledge-manager';
import { VoiceInput } from '@/components/ai/voice-input';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAppMode } from '@/hooks/use-app-mode';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

interface Conversation {
  id: string;
  title: string;
}

// Add this helper function to format the message content
function formatMessageContent(content: string): string {
  // Remove ## markers that might be present in the AI's response
  return content.replace(/##\s+/g, '');
}

export function FlexHelpAIPage() {
  const [messages, setMessages] = useState<AIChatMessage[]>([
    {
      id: uuidv4(),
      role: 'system',
      content: 'Welcome to FlexHelp AI! I can help with property management, maintenance questions, compliance regulations, and more. How can I assist you today?',
      timestamp: new Date().toISOString(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showKnowledgeManager, setShowKnowledgeManager] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([
    { id: 'new', title: 'New Conversation' }
  ]);
  const [activeConversation, setActiveConversation] = useState('new');
  const [voiceInputEnabled, setVoiceInputEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showVoiceUI, setShowVoiceUI] = useState(true); // Default to voice UI enabled
  const [voiceCommandsVisible, setVoiceCommandsVisible] = useState(false);
  const [ragStatus, setRagStatus] = useState({ initialized: false, documentCount: 0 });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const { mode, setMode } = useAppMode();
  const navigate = useNavigate();

  // Check if we're in the right mode, otherwise switch to AI mode
  useEffect(() => {
    if (mode !== 'ai') {
      setMode('ai'); // Automatically switch to AI mode when visiting this page
    }
  }, [mode, navigate, setMode]);

  // Try to initialize the RAG system on mount
  useEffect(() => {
    const initRAG = async () => {
      // First try to load from storage
      let success = await loadTrainingDataFromStorage();
      
      // If that fails, try to initialize with default data
      if (!success) {
        success = await initializeRAGSystem();
      }
      
      // Update RAG status regardless of outcome
      const initialized = vectorStore.isReady();
      const documentCount = vectorStore.getDocumentCount();
      setRagStatus({ initialized, documentCount });
      
      if (initialized) {
        console.log(`RAG system initialized with ${documentCount} documents`);
      } else {
        console.warn('RAG system could not be initialized properly');
      }
    };
    
    initRAG();
  }, []);

  // Check RAG status on component mount
  useEffect(() => {
    const checkRagStatus = async () => {
      const initialized = vectorStore.isReady();
      const documentCount = vectorStore.getDocumentCount();
      
      setRagStatus({ initialized, documentCount });
      
      if (initialized) {
        console.log(`RAG system initialized with ${documentCount} documents`);
      } else {
        console.log('RAG system not initialized or empty');
      }
    };
    
    // Check immediately and then again after a delay
    checkRagStatus();
    const timer = setTimeout(checkRagStatus, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input field when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSettings, showKnowledgeManager]);

  // Process voice commands
  const handleVoiceCommand = (text: string) => {
    // Basic command detection
    const command = text.toLowerCase().trim();
    
    if (command.includes('new conversation')) {
      startNewConversation();
      return true;
    } else if (command.includes('settings') || command.includes('open settings')) {
      setShowSettings(true);
      return true;
    } else if (command.includes('show commands') || command.includes('help')) {
      setVoiceCommandsVisible(true);
      return true;
    } else if (command.includes('hide commands')) {
      setVoiceCommandsVisible(false);
      return true;
    }
    
    // If not a command, treat as a question/input
    return false;
  };

  const processQuery = async (query: string) => {
    if (!query.trim()) return;
    
    // Add user message to chat
    const userMessage: AIChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: query,
      timestamp: new Date().toISOString(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Search for relevant documents if vector store is ready
      let contextFromDocs = '';
      
      // Update RAG system status
      const isRagReady = vectorStore.isReady();
      const docCount = vectorStore.getDocumentCount();
      setRagStatus({ initialized: isRagReady, documentCount: docCount });
      
      if (isRagReady) {
        console.log(`RAG system is ready with ${docCount} documents. Searching for content...`);
        // Increase the number of documents to retrieve for better context
        const relevantDocs = await vectorStore.similaritySearch(query, 5);
        console.log(`Found ${relevantDocs.length} relevant documents for query: "${query}"`);
        
        if (relevantDocs.length > 0) {
          console.log("Document contents:", relevantDocs.map(doc => doc.content.substring(0, 100) + "..."));
          contextFromDocs = `
Here are some relevant documents that may help answer the query:
    
${relevantDocs.map((doc, i) => `[Document ${i+1}]:\n${doc.content}`).join('\n\n')}

Use the information above as your primary source to answer the user's question. If the documents contain the information needed, always prioritize it over your general knowledge. Format your response in natural language without markdown or symbols like ## at the beginning of statements.`;
        } else {
          console.log("No relevant documents found in the knowledge base");
        }
      } else {
        console.log("Vector store is not ready or has no documents");
      }

      // Create OpenAI message payload
      const openAIMessages: OpenAIMessage[] = [
        {
          role: 'system',
          content: `You are FlexHelp AI, an assistant specialized in property management, maintenance guidance, and regulatory compliance for the FlexHelp platform.
          
Current user: ${user?.name} (${user?.role})
Time: ${new Date().toLocaleString()}

When asked about properties, maintenance issues, or compliance requirements:
1. Focus on providing practical, actionable advice
2. Reference industry best practices and regulations where applicable
3. Consider property type and location when giving compliance advice
4. Be conversational but professional
5. Prioritize user safety and regulatory compliance in your recommendations
6. Format your responses in natural language without using markdown headers (##) or other formatting symbols
7. When responding with factual information, provide it directly without prefacing with symbols

${contextFromDocs}`,
        },
        ...messages
          .filter(msg => msg.role !== 'system')
          .map(msg => ({ 
            role: msg.role === 'user' ? 'user' as const : 'assistant' as const, 
            content: msg.content 
          })),
        { role: 'user', content: query },
      ];

      // Generate response from OpenAI
      const aiResponse = await openAIService.generateCompletion(openAIMessages);

      if (aiResponse.error) {
        throw new Error(aiResponse.error);
      }

      // Add AI response to chat
      const assistantMessage: AIChatMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: aiResponse.content,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      // Create a new conversation title if this is the first message
      if (activeConversation === 'new' && conversations.length === 1) {
        // Generate a title based on the first user query
        const newConvId = uuidv4();
        const title = query.length > 20 ? query.substring(0, 20) + '...' : query;
        setConversations([
          { id: 'new', title: 'New Conversation' },
          { id: newConvId, title }
        ]);
        setActiveConversation(newConvId);
      }
    } catch (error) {
      console.error('Error processing query:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to get AI response',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    await processQuery(input);
  };

  const handleVoiceInput = (text: string) => {
    // Check if this is a command
    const isCommand = handleVoiceCommand(text);
    
    if (!isCommand) {
      // If not a command, set as input text
      setInput(text);
      toast({
        title: "Voice transcribed",
        description: "Your voice has been converted to text. Press send to submit or say 'send' to submit.",
      });
    }
  };

  const handleDataUploaded = () => {
    setShowSettings(false);
    
    try {
      localStorage.setItem('has-training-data', 'true');
    } catch (error) {
      console.warn('Error saving to localStorage:', error);
    }
    
    // Update RAG status
    const isReady = vectorStore.isReady();
    const docCount = vectorStore.getDocumentCount();
    setRagStatus({ initialized: isReady, documentCount: docCount });
    
    // Add a success message to the chat
    setMessages(prev => [
      ...prev, 
      {
        id: uuidv4(),
        role: 'system',
        content: `📚 Training data has been uploaded successfully! I can now provide more specific answers based on ${docCount} document chunks in the knowledge base.`,
        timestamp: new Date().toISOString(),
      }
    ]);
    
    toast({
      title: "Knowledge Base Updated",
      description: `The AI now has access to ${docCount} document chunks for answering questions.`,
    });
  };

  const startNewConversation = () => {
    setMessages([{
      id: uuidv4(),
      role: 'system',
      content: 'Welcome to FlexHelp AI! I can help with property management, maintenance questions, compliance regulations, and more. How can I assist you today?',
      timestamp: new Date().toISOString(),
    }]);
    setActiveConversation('new');
  };

  // Function to check if user can manage training data
  const canManageTrainingData = () => {
    return user?.role === 'admin' || 
           user?.role === 'property_manager' || 
           user?.role === 'property_owner';
  };

  // Start listening for voice input
  const startListening = () => {
    setIsListening(true);
  };

  // Stop listening for voice input
  const stopListening = () => {
    setIsListening(false);
  };

  // Toggle between voice and text UI
  const toggleVoiceUI = () => {
    setShowVoiceUI(!showVoiceUI);
    toast({
      title: showVoiceUI ? "Text mode activated" : "Voice mode activated",
      description: showVoiceUI ? "Switched to text input mode" : "Switched to voice command mode",
    });
  };

  // Voice Commands Guide Component
  const VoiceCommandsGuide = () => (
    <AnimatePresence>
      {voiceCommandsVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-20 right-4 z-10 bg-card/90 backdrop-blur-md p-4 rounded-lg shadow-lg border max-w-xs"
        >
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium text-sm">Voice Commands</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 w-6 p-0" 
              onClick={() => setVoiceCommandsVisible(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center">
              <Badge variant="outline" className="mr-2">New conversation</Badge>
              <span>Start a new chat</span>
            </div>
            <div className="flex items-center">
              <Badge variant="outline" className="mr-2">Settings</Badge>
              <span>Open settings menu</span>
            </div>
            <div className="flex items-center">
              <Badge variant="outline" className="mr-2">Show commands</Badge>
              <span>Show this guide</span>
            </div>
            <div className="flex items-center">
              <Badge variant="outline" className="mr-2">Hide commands</Badge>
              <span>Hide this guide</span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Settings Dialog Component
  const AISettingsDialog = () => {
    const [activeTab, setActiveTab] = useState<string>("general");
    
    // Generic handler to stop event propagation
    const stopProp = () => {
      // Empty handler to stop propagation via button props
    };
    
    return (
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>FlexHelp AI Settings</DialogTitle>
            <DialogDescription>
              Configure your AI assistant and manage training data
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab} className="mt-2" onClick={stopProp}>
            <TabsList className="grid w-full grid-cols-2" onClick={stopProp}>
              <TabsTrigger value="general" onClick={stopProp}>General</TabsTrigger>
              {canManageTrainingData() && (
                <TabsTrigger value="training-data" onClick={stopProp}>Training Data</TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="general" className="space-y-4 mt-4">
              {user?.role === 'admin' && (
                <Card className="p-4" onClick={stopProp}>
                  <div className="flex items-center gap-3 mb-3">
                    <Settings className="h-5 w-5 text-primary" />
                    <h3 className="font-medium">API Configuration</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Configure the OpenAI API key used by FlexHelp AI (Admin only)
                  </p>
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSettings(false);
                      setShowConfig(true);
                    }}
                  >
                    Configure API Key
                  </Button>
                </Card>
              )}
              
              <Card className="p-4" onClick={stopProp}>
                <div className="flex items-center gap-3 mb-3">
                  <Database className="h-5 w-5 text-primary" />
                  <h3 className="font-medium">Knowledge Base</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  View and manage existing training data in the knowledge base
                </p>
                <Button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSettings(false);
                    setShowKnowledgeManager(true);
                  }}
                >
                  Manage Knowledge Base
                </Button>
              </Card>

              <Card className="p-4" onClick={stopProp}>
                <div className="flex items-center gap-3 mb-3" onClick={stopProp}>
                  <Mic className="h-5 w-5 text-primary" />
                  <h3 className="font-medium">Voice Interface</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure voice input and interface settings
                </p>
                <div className="flex items-center space-x-2 mb-4" onClick={stopProp}>
                  <Switch
                    id="voice-input"
                    checked={voiceInputEnabled}
                    onCheckedChange={setVoiceInputEnabled}
                    onClick={stopProp}
                  />
                  <Label htmlFor="voice-input" onClick={stopProp}>
                    {voiceInputEnabled ? 'Voice input enabled' : 'Voice input disabled'}
                  </Label>
                </div>
                <div className="flex items-center space-x-2" onClick={stopProp}>
                  <Switch
                    id="voice-ui"
                    checked={showVoiceUI}
                    onCheckedChange={setShowVoiceUI}
                    onClick={stopProp}
                  />
                  <Label htmlFor="voice-ui" onClick={stopProp}>
                    {showVoiceUI ? 'Voice-optimized UI' : 'Standard UI'}
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Note: Voice transcription uses OpenAI's API and may incur additional costs
                </p>
              </Card>
            </TabsContent>
            
            {canManageTrainingData() && (
              <TabsContent value="training-data" className="space-y-4 mt-4" onClick={stopProp}>
                <TrainingDataUploader onComplete={handleDataUploaded} />
              </TabsContent>
            )}
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={(e) => {
              e.stopPropagation();
              setShowSettings(false);
            }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Show developer configuration screen if settings clicked
  if (showConfig) {
    return <AIConfig onConfigured={() => {
      setShowConfig(false);
      toast({
        title: "API Configuration Updated",
        description: "The OpenAI API configuration has been updated successfully.",
      });
    }} />;
  }

  // Show knowledge manager if requested
  if (showKnowledgeManager) {
    return <KnowledgeManager onClose={() => setShowKnowledgeManager(false)} />;
  }

  return (
    <div className={`flex h-[calc(100vh-4rem)] ${showVoiceUI ? 'bg-gradient-to-br from-indigo-50/50 via-background to-purple-50/50 dark:from-indigo-950/20 dark:via-background dark:to-purple-950/20' : ''}`}>
      {/* Settings Dialog and Voice Commands Guide */}
      <AISettingsDialog />
      <VoiceCommandsGuide />
      
      {/* AI-Specific Sidebar - Redesigned for Voice UI */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className={`${showVoiceUI ? 'w-72 bg-gradient-to-b from-card/90 to-card/70 backdrop-blur-lg' : 'w-64 border-r bg-card/50 backdrop-blur-sm'} flex flex-col`}
      >
        {/* Header with AI branding */}
        <div className={`${showVoiceUI ? 'p-6 border-b border-primary/10' : 'p-4 border-b'} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className={`${showVoiceUI ? 'rounded-xl bg-primary/20 p-3' : 'rounded-full bg-primary/10 p-2'} flex items-center justify-center`}>
              <Sparkles className={`${showVoiceUI ? 'h-5 w-5' : 'h-4 w-4'} text-primary`} />
            </div>
            <div>
              <h2 className={`font-medium ${showVoiceUI ? 'text-lg' : ''}`}>FlexHelp AI</h2>
              {showVoiceUI && (
                <p className="text-xs text-muted-foreground">
                  Voice-enhanced assistant
                </p>
              )}
              
              {/* RAG System Status Indicator */}
              <div className="flex items-center mt-1 text-xs">
                <span className={`inline-block w-2 h-2 rounded-full mr-1 ${ragStatus.initialized ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                <span className="text-muted-foreground">
                  {ragStatus.initialized 
                    ? `Knowledge base: ${ragStatus.documentCount} docs` 
                    : "No knowledge base loaded"}
                </span>
              </div>
            </div>
          </div>
          
          {/* UI Mode Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={toggleVoiceUI}
                  className={showVoiceUI ? 'bg-primary/10 text-primary hover:bg-primary/20' : ''}
                >
                  {showVoiceUI ? (
                    <Mic className="h-4 w-4" />
                  ) : (
                    <MessageSquare className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {showVoiceUI ? 'Switch to text interface' : 'Switch to voice interface'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Conversations List */}
        <ScrollArea className="flex-1">
          <div className={`${showVoiceUI ? 'p-4' : 'p-2'} space-y-2`}>
            {showVoiceUI && (
              <div className="mb-4">
                <h3 className="text-xs uppercase font-semibold text-muted-foreground mb-2 px-2">Conversations</h3>
              </div>
            )}
            
            {/* New Conversation Button - More prominent in Voice UI */}
            <Button
              variant={showVoiceUI ? "default" : "ghost"}
              className={`w-full justify-start ${showVoiceUI ? 'h-12 mb-3' : ''}`}
              onClick={startNewConversation}
            >
              <PlusCircle className={`${showVoiceUI ? 'h-5 w-5 mr-3' : 'h-4 w-4 mr-2'}`} />
              <span>New Conversation</span>
            </Button>
            
            {conversations.filter(conv => conv.id !== 'new').map((conversation) => (
              <Button
                key={conversation.id}
                variant={activeConversation === conversation.id ? (showVoiceUI ? "secondary" : "secondary") : (showVoiceUI ? "ghost" : "ghost")}
                className={`w-full justify-start overflow-hidden ${showVoiceUI ? 'h-12 mb-1' : ''}`}
                onClick={() => setActiveConversation(conversation.id)}
              >
                <MessageSquare className={`${showVoiceUI ? 'h-5 w-5 mr-3' : 'h-4 w-4 mr-2'} flex-shrink-0`} />
                <span className="truncate">{conversation.title}</span>
              </Button>
            ))}
          </div>
        </ScrollArea>
        
        {/* Voice UI Features Section */}
        {showVoiceUI && (
          <div className="p-4 space-y-3">
            <h3 className="text-xs uppercase font-semibold text-muted-foreground mb-2 px-2">Voice Commands</h3>
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline"
                size="sm"
                className="justify-start text-xs"
                onClick={() => handleVoiceCommand("new conversation")}
              >
                <PlusCircle className="h-3 w-3 mr-2" />
                New Chat
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="justify-start text-xs"
                onClick={() => handleVoiceCommand("settings")}
              >
                <Settings className="h-3 w-3 mr-2" />
                Settings
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="justify-start text-xs"
                onClick={() => setVoiceCommandsVisible(true)}
              >
                <Mic className="h-3 w-3 mr-2" />
                Commands
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="justify-start text-xs"
                onClick={() => handleVoiceCommand("help")}
              >
                <MessageSquare className="h-3 w-3 mr-2" />
                Help
              </Button>
            </div>
          </div>
        )}
        
        {/* Footer with Settings & Status */}
        <div className={`${showVoiceUI ? 'p-6 border-t border-primary/10' : 'p-4 border-t'} space-y-2`}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowSettings(true);
                  }}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  <span>Settings</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Manage AI Settings</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Knowledge Base Status */}
          {vectorStore.isReady() && (
            <div className={`flex items-center justify-center gap-2 text-xs text-muted-foreground ${showVoiceUI ? 'mt-3 p-3 bg-card/50 rounded-lg' : 'mt-1 p-2'}`}>
              <Database className="h-3 w-3" />
              <span>{vectorStore.getDocumentCount()} documents in knowledge base</span>
              {vectorStore.isUsingEmbeddings() && (
                <Badge variant="outline" className="ml-1 h-5 px-1 rounded-sm text-[10px]">
                  <Sparkles className="h-2 w-2 mr-1" />
                  Embeddings
                </Badge>
              )}
            </div>
          )}
          
          {/* Storage Type */}
          {showVoiceUI && (
            <div className="text-xs text-center text-muted-foreground mt-2">
              {isUsingSupabase() ? (
                <span className="flex items-center justify-center gap-1">
                  <Cloud className="h-3 w-3 text-blue-500" />
                  Using persistent storage
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1">
                  <HardDrive className="h-3 w-3 text-orange-500" />
                  Using in-memory storage
                </span>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Chat UI */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex-1 flex flex-col relative overflow-hidden"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 -z-10 bg-grid-black/5 dark:bg-grid-white/5">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
        </div>
        
        {/* Messages Area */}
        <ScrollArea className="flex-1 p-4">
          <div className={`${showVoiceUI ? 'max-w-5xl' : 'max-w-4xl'} mx-auto space-y-6`}>
            <AnimatePresence initial={false}>
              {messages.map((message, index) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col"
                >
                  {message.role === 'system' ? (
                    <Card variant="glass" className="max-w-[90%] mx-auto">
                      <div className="px-4 py-3 text-center text-sm">
                        {message.content}
                      </div>
                    </Card>
                  ) : (
                    <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`flex gap-3 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                        <Avatar className={message.role === 'assistant' ? 'bg-primary/10' : undefined}>
                          {message.role === 'assistant' ? (
                            <>
                              <AvatarImage src="/ai-assistant.png" />
                              <AvatarFallback className="bg-primary/10">
                                <Bot className="h-5 w-5 text-primary" />
                              </AvatarFallback>
                            </>
                          ) : (
                            <>
                              <AvatarImage src={user?.avatar} />
                              <AvatarFallback>
                                {user?.name?.[0] || 'U'}
                              </AvatarFallback>
                            </>
                          )}
                        </Avatar>
                        <div className={`${
                          message.role === 'assistant' 
                            ? 'bg-card/60 backdrop-blur-sm text-card-foreground rounded-r-xl rounded-bl-xl' 
                            : 'bg-primary text-primary-foreground rounded-l-xl rounded-br-xl'
                          } px-4 py-3 shadow-sm`}
                        >
                          <div className="whitespace-pre-wrap break-words">
                            {message.role === 'assistant' ? formatMessageContent(message.content) : message.content}
                          </div>
                          <div className="text-xs opacity-70 mt-1 text-right">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="flex gap-3 max-w-[80%]">
                    <Avatar className="bg-primary/10">
                      <AvatarFallback>
                        <Bot className="h-5 w-5 text-primary" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-card/60 backdrop-blur-sm rounded-r-xl rounded-bl-xl px-4 py-3 flex items-center">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <span className="ml-3">Thinking...</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area - Redesigned for Voice UI */}
        <div className={`${showVoiceUI ? 'border-t border-primary/10 bg-card/80' : 'border-t bg-background/80'} backdrop-blur-sm p-4`}>
          {showVoiceUI ? (
            <div className="max-w-5xl mx-auto">
              {/* Voice-optimized input */}
              <div className="flex gap-3 items-center">
                <div className="flex-1 relative">
                  <form onSubmit={handleSend} className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Type your message or click the mic to speak..."
                      className="flex-1 bg-background/50 border-muted-foreground/20 pr-10 h-12"
                    />
                    <Button 
                      type="submit" 
                      className="h-12 px-4"
                      disabled={isLoading || !input.trim()}
                    >
                      {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    </Button>
                  </form>
                </div>
                
                {/* Large voice input button */}
                {voiceInputEnabled && (
                  <VoiceInput
                    onTranscription={handleVoiceInput}
                    isDisabled={isLoading || !openAIService.isReady()}
                    isListening={isListening}
                    onListeningStart={() => setIsListening(true)}
                    onListeningEnd={() => setIsListening(false)}
                  />
                )}
              </div>
              
              {/* Animation for voice listening */}
              {isListening && (
                <div className="flex justify-center items-center my-2">
                  <div className="flex gap-1 items-center">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="bg-primary w-1 rounded-full"
                        animate={{
                          height: [8, 16, 8],
                        }}
                        transition={{
                          duration: 0.8,
                          repeat: Infinity,
                          delay: i * 0.1,
                        }}
                      />
                    ))}
                  </div>
                  <span className="ml-3 text-sm text-primary">Listening...</span>
                </div>
              )}
              
              <div className="flex justify-between items-center mt-3">
                <div className="text-xs text-muted-foreground">
                  FlexHelp AI is designed to assist with property management.
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs text-muted-foreground"
                  onClick={() => setVoiceCommandsVisible(!voiceCommandsVisible)}
                >
                  <span>{voiceCommandsVisible ? 'Hide Commands' : 'Show Commands'}</span>
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSend} className="max-w-4xl mx-auto flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-background/50 border-muted-foreground/20"
              />
              {voiceInputEnabled && (
                <VoiceInput 
                  onTranscription={handleVoiceInput} 
                  isDisabled={isLoading || !openAIService.isReady()} 
                />
              )}
              <Button type="submit" disabled={isLoading || !input.trim()}>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          )}
          {!showVoiceUI && (
            <div className="text-xs text-center text-muted-foreground mt-2">
              FlexHelp AI is designed to assist with property management. Your interactions help improve our service.
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
} 